import { AccountService } from '@fp/interfaces/conversational/generated/api/account.service';
import { ConversationModel } from '@klant/conversational-connector/models/Conversation.model';
import { ConfigResponse } from '@fp/interfaces/conversational/generated/model/config-response';
import { ConversationService } from '@klant/conversational-connector/services/conversation/conversation.service';

import { Injectable, signal, inject } from '@angular/core';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ChannelName, MijnKanaalconfiguratieService } from '@ap/mijn-kanaalconfiguratie';

import { SessionStorageService } from '../../session-storage/session-storage.service';

import { CONVERSATIONAL_CONFIGURATION_DEFAULT, CONVERSATIONAL_ENVIRONMENT } from './klant-conversational-connector.constants';
import { ChatSession, SeamlyAPIContext, SeamlyConfiguration, LoginStatus } from './klant-conversational-connector.model';

@Injectable({ providedIn: 'root' })
export class KlantConversationalConnectorService {
    private readonly mijnKanaalconfiguratieService = inject(MijnKanaalconfiguratieService);
    private readonly accountService = inject(AccountService);
    private readonly conversationService = inject(ConversationService);
    private readonly sessionStorageService = inject(SessionStorageService);

    public chatFullyLoaded: boolean;
    public firstMessageWasSent: boolean;
    public faqMessage$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    private previouslySentFaqQuestion = signal<string>(null);

    public getPreviouslySentFaqQuestion = this.previouslySentFaqQuestion.asReadonly();

    public get wasFirstMessageSent(): boolean {
        return this.firstMessageWasSent;
    }

    public setPreviouslySentFaqQuestion(faqQuestion: string) {
        this.previouslySentFaqQuestion.set(faqQuestion);
    }

    public setFaqMessage(message: string) {
        this.faqMessage$.next(message);
    }

    public onFirstMessageSent(isFirst: boolean): void {
        this.firstMessageWasSent = isFirst;
    }

    public initializeSeamly(chatSession: ChatSession): Observable<ConversationModel> {
        const isChatAlreadyStartedInThisSession: boolean = !!this.sessionStorageService.getConversationSessionDetails();

        if (isChatAlreadyStartedInThisSession && chatSession.loginStatus === LoginStatus.LOGGED_IN) {
            return this.resumeConversation(chatSession);
        }

        return this.createConversation(chatSession);
    }

    private resumeConversation(chatSession: ChatSession): Observable<ConversationModel> {
        return this.conversationService.getConversation().pipe(
            catchError((error) => {
                if (error && error.status === 404) {
                    return this.createConversation(chatSession);
                }
                throw error;
            })
        );
    }

    private createConversation(chatSession: ChatSession): Observable<ConversationModel> {
        return this.getSeamlyConfiguration().pipe(
            tap((seamlyConfiguration: SeamlyConfiguration) => {
                this.sessionStorageService.updateSeamlyEnvironmentConfig({
                    seamlyEnvironment: seamlyConfiguration.environment,
                    seamlyApiDomain: seamlyConfiguration.seamlyApiDomain
                });
            }),
            switchMap((seamlyConfiguration: SeamlyConfiguration) => {
                return this.getConversationLink(seamlyConfiguration.seamlyApiKey);
            }),
            switchMap((conversationLink: string) => {
                return this.conversationService.createConversation(conversationLink, chatSession);
            }),
            tap((conversation: ConversationModel) => {
                if (chatSession.loginStatus === LoginStatus.LOGGED_IN) {
                    this.sessionStorageService.updateConversationSessionDetails({
                        conversationLink: conversation.conversationLink,
                        accessToken: conversation.accessToken
                    });
                }
            })
        );
    }

    private getSeamlyConfiguration(): Observable<SeamlyConfiguration> {
        return this.mijnKanaalconfiguratieService.getJSONConfiguration<SeamlyConfiguration>(
            ChannelName.MIJN_KLANT,
            CONVERSATIONAL_ENVIRONMENT,
            CONVERSATIONAL_CONFIGURATION_DEFAULT,
            false
        );
    }

    private getConversationLink(apiKey: string): Observable<string> {
        return this.accountService
            .getAccountConfig({
                apiKey,
                getAccountConfigRequest: SeamlyAPIContext
            })
            .pipe(
                map((configResponse: ConfigResponse) => {
                    return configResponse._links.conversations.href;
                })
            );
    }
}
